<template>
  <div class="movimientos-internos-container">
    <!-- Header -->
    <v-card>
      <v-card-title class="d-flex flex-column flex-md-row justify-space-between align-start align-md-center">
        <div class="mb-3 mb-md-0">
          <span>Movimientos Internos</span>
          <div class="text-caption text-grey mt-1">
            Transferencias entre ubicaciones
          </div>
        </div>
        <div class="d-flex gap-2">
          <v-btn
            color="primary"
            prepend-icon="mdi-swap-horizontal"
            @click="showNewMovementModal"
            size="default"
          >
            Nuevo Movimiento
          </v-btn>
          <v-btn
            color="success"
            prepend-icon="mdi-microsoft-excel"
            @click="exportMovements"
            :loading="exporting"
            size="default"
            variant="outlined"
          >
            Exportar
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <!-- Filtros -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              label="Estado"
              hide-details
              density="compact"
              clearable
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.dateFrom"
              label="Fecha desde"
              type="date"
              hide-details
              density="compact"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              label="Buscar"
              placeholder="Referencia, producto..."
              hide-details
              density="compact"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Tabla de movimientos -->
        <ResponsiveDataTable
          :items="filteredMovements"
          :headers="headers"
          :loading="loading"
          :search="filters.search"
          class="elevation-1"
          :show-select="false"
          items-per-page="15"
          density="default"
          hover
        >
          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:item.source_warehouse="{ item }">
            <div v-if="item.source_warehouse">
              <div class="font-weight-medium">{{ item.source_warehouse.name }}</div>
              <div class="text-caption text-grey">{{ item.source_warehouse.code }}</div>
            </div>
            <span v-else class="text-grey">-</span>
          </template>

          <template v-slot:item.source_zone="{ item }">
            <div v-if="item.source_zone">
              <div class="font-weight-medium">{{ item.source_zone.name }}</div>
              <div class="text-caption text-grey">{{ item.source_zone.code }}</div>
            </div>
            <span v-else class="text-grey">-</span>
          </template>

          <template v-slot:item.dest_warehouse="{ item }">
            <div v-if="item.dest_warehouse">
              <div class="font-weight-medium">{{ item.dest_warehouse.name }}</div>
              <div class="text-caption text-grey">{{ item.dest_warehouse.code }}</div>
            </div>
            <span v-else class="text-grey">-</span>
          </template>

          <template v-slot:item.dest_zone="{ item }">
            <div v-if="item.dest_zone">
              <div class="font-weight-medium">{{ item.dest_zone.name }}</div>
              <div class="text-caption text-grey">{{ item.dest_zone.code }}</div>
            </div>
            <span v-else class="text-grey">-</span>
          </template>

          <template v-slot:item.products_count="{ item }">
            <div class="text-center">
              <v-chip size="small" color="info">
                {{ item.details?.length || 0 }} productos
              </v-chip>
            </div>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              size="small"
            >
              {{ item.status }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon="mdi-eye"
              size="small"
              variant="text"
              color="info"
              @click="viewMovement(item)"
            ></v-btn>
            <v-btn
              v-if="item.status === 'Pendiente'"
              icon="mdi-check"
              size="small"
              variant="text"
              color="success"
              @click="executeMovement(item)"
              title="Ejecutar Movimiento"
            ></v-btn>
            <v-btn
              v-if="item.status === 'Pendiente'"
              icon="mdi-delete"
              size="small"
              variant="text"
              color="error"
              @click="deleteMovement(item)"
            ></v-btn>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-4">
              <v-icon icon="mdi-swap-horizontal" color="grey" size="large"></v-icon>
              <p class="text-subtitle-1 mt-2">No se encontraron movimientos</p>
            </div>
          </template>
        </ResponsiveDataTable>
      </v-card-text>
    </v-card>

    <!-- Modal de Nuevo Movimiento -->
    <v-dialog v-model="showMovementDialog" max-width="1200" persistent>
      <v-card>
        <v-card-title>
          Nuevo Movimiento Interno
        </v-card-title>
        <v-card-text>
          <v-form ref="movementForm">
            <!-- Tipo de movimiento -->
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="movementForm.movement_type"
                  :items="movementTypes"
                  label="Tipo de Movimiento"
                  variant="outlined"
                  density="compact"
                  :rules="[v => !!v || 'El tipo es requerido']"
                  required
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-textarea
                  v-model="movementForm.notes"
                  label="Notas/Motivo"
                  variant="outlined"
                  density="compact"
                  rows="2"
                  placeholder="Ej: Optimización de espacio, reubicación..."
                ></v-textarea>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Ubicaciones Origen y Destino (GLOBALES) -->
            <v-alert
              type="info"
              variant="tonal"
              density="compact"
              class="mb-4"
            >
              <strong>Importante:</strong> Seleccione las ubicaciones de origen y destino. Estas se aplicarán a todos los productos escaneados.
            </v-alert>

            <v-row>
              <!-- ORIGEN -->
              <v-col cols="12" md="6">
                <v-card variant="outlined" class="pa-3">
                  <h4 class="mb-3">📤 Origen</h4>
                  <v-select
                    v-model="movementForm.source_warehouse_id"
                    :items="warehouses"
                    item-title="name"
                    item-value="id"
                    label="Bodega de Origen *"
                    variant="outlined"
                    density="compact"
                    :rules="[v => !!v || 'Bodega es requerida']"
                    @update:model-value="onSourceWarehouseChanged"
                    prepend-inner-icon="mdi-warehouse"
                    required
                  ></v-select>
                  
                  <v-select
                    v-model="movementForm.source_zone_id"
                    :items="sourceZones"
                    item-title="name"
                    item-value="id"
                    label="Zona de Origen *"
                    variant="outlined"
                    density="compact"
                    :rules="[v => !!v || 'Zona es requerida']"
                    :disabled="!movementForm.source_warehouse_id"
                    prepend-inner-icon="mdi-map-marker"
                    required
                    class="mt-2"
                  ></v-select>
                </v-card>
              </v-col>

              <!-- DESTINO -->
              <v-col cols="12" md="6">
                <v-card variant="outlined" class="pa-3">
                  <h4 class="mb-3">📥 Destino</h4>
                  <v-select
                    v-model="movementForm.dest_warehouse_id"
                    :items="warehouses"
                    item-title="name"
                    item-value="id"
                    label="Bodega de Destino *"
                    variant="outlined"
                    density="compact"
                    :rules="[v => !!v || 'Bodega es requerida']"
                    @update:model-value="onDestWarehouseChanged"
                    prepend-inner-icon="mdi-warehouse"
                    required
                  ></v-select>
                  
                  <v-select
                    v-model="movementForm.dest_zone_id"
                    :items="destZones"
                    item-title="name"
                    item-value="id"
                    label="Zona de Destino *"
                    variant="outlined"
                    density="compact"
                    :rules="[v => !!v || 'Zona es requerida']"
                    :disabled="!movementForm.dest_warehouse_id"
                    prepend-inner-icon="mdi-map-marker"
                    required
                    class="mt-2"
                  ></v-select>
                </v-card>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- Alerta si no se han seleccionado ubicaciones -->
            <v-alert
              v-if="!areLocationsSelected"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-4"
            >
              <v-icon start>mdi-alert</v-icon>
              Debe seleccionar <strong>Bodegas y Zonas</strong> de origen y destino antes de agregar productos.
            </v-alert>

            <!-- Productos -->
            <div class="d-flex justify-space-between align-center mb-4">
              <h3>Productos a Mover</h3>
              <v-btn
                color="secondary"
                prepend-icon="mdi-barcode"
                @click="startScan"
                size="small"
                variant="outlined"
                :disabled="loadingInitialData || !areLocationsSelected"
              >
                Escanear Productos
              </v-btn>
            </div>

            <!-- Tabla de productos -->
            <v-card variant="outlined" class="mb-4" v-if="movementForm.items.length > 0">
              <v-card-text class="pa-2">
                <v-table class="movement-items-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>Producto</th>
                        <th>SKU</th>
                        <th>Cantidad</th>
                        <th>Lote</th>
                        <th>Vencimiento</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in movementForm.items" :key="index">
                        <td>{{ item.product_name || 'Producto no encontrado' }}</td>
                        <td>{{ item.product_sku || '-' }}</td>
                        <td>{{ item.quantity }}</td>
                        <td>{{ item.lot_number || '-' }}</td>
                        <td>{{ formatDate(item.expiry_date) || '-' }}</td>
                        <td>
                          <v-btn
                            icon="mdi-delete"
                            size="small"
                            @click="removeProduct(index)"
                            color="error"
                            variant="text"
                          ></v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-table>
              </v-card-text>
            </v-card>

            <!-- Mensaje cuando no hay productos -->
            <v-alert
              v-if="movementForm.items.length === 0"
              type="info"
              variant="tonal"
              class="mb-4"
            >
              No hay productos agregados. Use el botón de escaneo para agregar productos.
            </v-alert>

            <!-- Panel de escaneo -->
            <v-card 
              v-if="showBarcodeScanner"
              variant="outlined"
              class="mb-4"
            >
              <v-card-title class="text-h6">
                Escaneo de Productos
              </v-card-title>
              <v-card-text>
                <v-textarea
                  ref="scannerInput"
                  v-model="scanInput"
                  label="Escanee o ingrese código"
                  rows="2"
                  variant="outlined"
                  density="compact"
                  @keyup.enter="processScannedBarcode(scanInput)"
                  hint="Presione Enter para procesar"
                  persistent-hint
                ></v-textarea>
                
                <div class="d-flex gap-2 mt-3">
                  <v-btn
                    color="primary"
                    @click="processScannedBarcode(scanInput)"
                    :disabled="!scanInput.trim()"
                  >
                    Procesar Código
                  </v-btn>
                  <v-btn
                    color="grey"
                    @click="closeBarcodeScanner"
                    variant="outlined"
                  >
                    Cancelar
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <!-- Loading inicial -->
            <v-card variant="outlined" v-if="loadingInitialData" class="mb-4">
              <v-card-text class="text-center">
                <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
                <div>Cargando datos del sistema...</div>
              </v-card-text>
            </v-card>

            <!-- Resumen -->
            <v-card variant="outlined" v-if="movementForm.items.length > 0">
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="8">
                    <div class="text-h6">Resumen</div>
                  </v-col>
                  <v-col cols="12" md="4" class="text-right">
                    <div>
                      <strong>Total de SKUs:</strong> {{ movementForm.items.length }}
                    </div>
                    <div>
                      <strong>Total de Unidades:</strong> {{ totalQuantity }}
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="closeMovementDialog">Cancelar</v-btn>
          <v-btn
            color="primary"
            @click="saveMovement"
            :loading="submitting"
            :disabled="!isMovementFormValid"
          >
            Crear Movimiento
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Vista de Movimiento -->
    <v-dialog v-model="showViewDialog" max-width="800">
      <v-card v-if="selectedMovement">
        <v-card-title>
          Detalle del Movimiento - {{ selectedMovement.reference }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-card variant="outlined" class="pa-3">
                <h4>Información General</h4>
                <div class="mt-2">
                  <strong>Fecha:</strong> {{ formatDate(selectedMovement.created_at) }}
                </div>
                <div>
                  <strong>Estado:</strong> 
                  <v-chip :color="getStatusColor(selectedMovement.status)" size="small" class="ml-2">
                    {{ selectedMovement.status }}
                  </v-chip>
                </div>
                <div v-if="selectedMovement.notes">
                  <strong>Notas:</strong> {{ selectedMovement.notes }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <h4>Productos</h4>
          <ResponsiveDataTable
            :items="selectedMovement.details || []"
            :headers="viewHeaders"
            class="elevation-1 mt-2"
            density="compact"
          >
            <template v-slot:item.product="{ item }">
              <div>
                <div class="font-weight-medium">{{ item.product?.name }}</div>
                <div class="text-caption text-grey">{{ item.product?.sku }}</div>
              </div>
            </template>
          </ResponsiveDataTable>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showViewDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para notificaciones -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn
          color="white"
          variant="text"
          @click="snackbar.show = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as XLSX from 'xlsx'
import api from '@/api'
import { findProductByAnyBarcode } from '@/services/productBarcodes'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'

export default {
  name: 'MovimientosInternos',
  components: {
    ResponsiveDataTable
  },
  data() {
    return {
      // Estado principal
      loading: false,
      exporting: false,
      submitting: false,
      showMovementDialog: false,
      showViewDialog: false,
      selectedMovement: null,
      
      // Filtros
      filters: {
        status: '',
        dateFrom: '',
        search: ''
      },

      // Opciones
      statusOptions: [
        { title: 'Pendiente', value: 'Pendiente' },
        { title: 'Completado', value: 'Completado' },
        { title: 'Cancelado', value: 'Cancelado' }
      ],

      movementTypes: [
        { title: 'Reubicación', value: 'reubicacion' },
        { title: 'Optimización', value: 'optimizacion' },
        { title: 'Transferencia entre Zonas', value: 'transferencia_zona' }
      ],

      // Headers de tablas
      headers: [
        { title: 'Referencia', key: 'reference', align: 'start', sortable: true },
        { title: 'Fecha', key: 'created_at', align: 'start', sortable: true },
        { title: 'Bodega Origen', key: 'source_warehouse', align: 'start', sortable: true },
        { title: 'Zona Origen', key: 'source_zone', align: 'start', sortable: true },
        { title: 'Bodega Destino', key: 'dest_warehouse', align: 'start', sortable: true },
        { title: 'Zona Destino', key: 'dest_zone', align: 'start', sortable: true },
        { title: 'Productos', key: 'products_count', align: 'center', sortable: false },
        { title: 'Estado', key: 'status', align: 'center', sortable: true },
        { title: 'Acciones', key: 'actions', align: 'center', sortable: false }
      ],

      viewHeaders: [
        { title: 'Producto', key: 'product', align: 'start' },
        { title: 'Cantidad', key: 'quantity', align: 'center' },
        { title: 'Lote', key: 'lot_number', align: 'start' }
      ],

      // Formulario de movimiento
      movementForm: {
        movement_type: '',
        notes: '',
        source_warehouse_id: null,
        source_zone_id: null,
        dest_warehouse_id: null,
        dest_zone_id: null,
        items: []
      },

      // Datos
      movements: [],
      allProducts: [],
      inventoryData: [],
      loadingInitialData: false,
      
      // Escaneo
      showBarcodeScanner: false,
      scanInput: '',
      
      // Notificaciones
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000
      },

      // GS1 Parser Data (copiado de Egresos)
      GS: String.fromCharCode(29),
      AI_DEFS: [
        { key: "00",  len: 18, fixed: true,  name: "SSCC" },
        { key: "01",  len: 14, fixed: true,  name: "GTIN" },
        { key: "02",  len: 14, fixed: true,  name: "Content GTIN" },
        { key: "11",  len: 6,  fixed: true,  name: "Prod date (YYMMDD)" },
        { key: "13",  len: 6,  fixed: true,  name: "Pack date (YYMMDD)" },
        { key: "15",  len: 6,  fixed: true,  name: "Best before (YYMMDD)" },
        { key: "17",  len: 6,  fixed: true,  name: "Expiry (YYMMDD)" },
        { key: "10", fixed: false, name: "Batch/Lot", max: 20 },
        { key: "21", fixed: false, name: "Serial",    max: 20 },
        { key: "30",  fixed: false, name: "Qty variable measure", max: 8 },
        { key: "37",  fixed: false, name: "Count", max: 8 }
      ]
    }
  },
  
  computed: {
    ...mapGetters({
      warehouses: 'warehouses/allWarehouses',
      zones: 'zones/allZones'
    }),
    
    filteredMovements() {
      let result = this.movements || []
      
      if (this.filters.status) {
        result = result.filter(movement => movement.status === this.filters.status)
      }
      
      if (this.filters.dateFrom) {
        const fromDate = new Date(this.filters.dateFrom)
        result = result.filter(movement => {
          const movementDate = new Date(movement.created_at)
          return movementDate >= fromDate
        })
      }
      
      return result
    },

    sourceZones() {
      if (!this.movementForm.source_warehouse_id) return []
      return this.zones.filter(z => z.warehouse_id === this.movementForm.source_warehouse_id)
    },

    destZones() {
      if (!this.movementForm.dest_warehouse_id) return []
      return this.zones.filter(z => z.warehouse_id === this.movementForm.dest_warehouse_id)
    },

    areLocationsSelected() {
      return !!(this.movementForm.source_warehouse_id && 
                this.movementForm.source_zone_id &&
                this.movementForm.dest_warehouse_id &&
                this.movementForm.dest_zone_id)
    },

    isMovementFormValid() {
      return this.movementForm.movement_type && 
             this.areLocationsSelected &&
             this.movementForm.items.length > 0
    },

    totalQuantity() {
      return this.movementForm.items.reduce((sum, item) => sum + (item.quantity || 0), 0)
    }
  },

  async created() {
    await Promise.all([
      this.$store.dispatch('warehouses/fetchWarehouses'),
      this.$store.dispatch('zones/fetchZones'),
      this.loadMovements()
    ])
  },

  methods: {

    // Cargar datos
    async loadInitialData() {
      if (this.loadingInitialData) return
      
      try {
        this.loadingInitialData = true
        console.log('🔄 Cargando datos iniciales...')
        
        const [productsResponse, inventoryResponse] = await Promise.all([
          api.get('/products'),
          api.get('/inventory')
        ])
        
        this.allProducts = productsResponse.data || []
        this.inventoryData = inventoryResponse.data || []
        
        console.log(`✅ Datos cargados: ${this.allProducts.length} productos, ${this.inventoryData.length} items de inventario`)
        
      } catch (error) {
        console.error('❌ Error cargando datos iniciales:', error)
        this.showError('Error al cargar datos del sistema')
      } finally {
        this.loadingInitialData = false
      }
    },

    async loadMovements() {
      try {
        this.loading = true
        const response = await api.getMovimientosInternos()
        this.movements = response.data || []
      } catch (error) {
        console.error('Error loading movements:', error)
        this.showError('Error al cargar los movimientos')
      } finally {
        this.loading = false
      }
    },

    // Modal handlers
    async showNewMovementModal() {
      this.resetMovementForm()
      this.showMovementDialog = true
      
      // Cargar datos necesarios para el escaneo
      await this.loadInitialData()
    },

    closeMovementDialog() {
      this.showMovementDialog = false
      this.resetMovementForm()
    },

    resetMovementForm() {
      this.movementForm = {
        movement_type: '',
        notes: '',
        source_warehouse_id: null,
        source_zone_id: null,
        dest_warehouse_id: null,
        dest_zone_id: null,
        items: []
      }
      this.closeBarcodeScanner()
    },

    // Warehouse handlers
    onSourceWarehouseChanged() {
      // Reset zone selection when warehouse changes
      this.movementForm.source_zone_id = null
    },

    onDestWarehouseChanged() {
      // Reset zone selection when warehouse changes
      this.movementForm.dest_zone_id = null
    },

    // Barcode scanning
    startScan() {
      this.showBarcodeScanner = true
      this.scanInput = ''
      
      // Focus input
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.scannerInput && this.$refs.scannerInput.$el) {
            const textarea = this.$refs.scannerInput.$el.querySelector('textarea')
            if (textarea) {
              textarea.focus()
              console.log('✅ Scanner input focused')
            }
          }
        }, 100)
      })
    },

    closeBarcodeScanner() {
      this.showBarcodeScanner = false
      this.scanInput = ''
    },

    async processScannedBarcode(code) {
      if (!code || !code.trim()) return
      
      const cleanedCode = code.trim()
      console.log(`🔍 Procesando código de barras: ${cleanedCode}`)
      
      // Verificar que los datos estén cargados
      if (this.allProducts.length === 0) {
        console.log('⚠️ Los datos no están cargados, cargando...')
        await this.loadInitialData()
      }
      
      try {
        // Analizar el código
        const analysis = this.analyzeBarcode(cleanedCode)
        console.log('📊 Análisis del código:', analysis)
        
        let product = null
        let productData = {}
        
        if (analysis.type === 'GS1') {
          console.log(`🏷️ Código GS1 detectado, GTIN: ${analysis.gtin}`)
          product = this.findProductByGTIN(analysis.gtin)
          productData = analysis.variableData || {}
          console.log('📦 Datos variables extraídos:', productData)
        } else {
          console.log(`📋 Código estándar detectado: ${cleanedCode}`)
          product = this.findProductByBarcode(cleanedCode)
        }
        
        if (product) {
          console.log(`✅ Producto encontrado: ${product.name} (ID: ${product.id})`)
          await this.addScannedProduct(product, productData, cleanedCode)
        } else {
          console.log(`❌ Producto NO encontrado para código: ${cleanedCode}`)
          this.showError(`Producto no encontrado para el código: ${cleanedCode}`)
        }
        
      } catch (error) {
        console.error('❌ Error procesando código:', error)
        this.showError(`Error procesando código: ${cleanedCode}`)
      }
      
      // Limpiar input
      this.scanInput = ''
    },

    // COPIED FROM EGRESOS: Barcode analysis functions
    analyzeBarcode(barcode) {
      if (!barcode || barcode.trim() === '') {
        return { type: 'Sin código', gtin: null, original: barcode, variableData: null }
      }
      
      const cleaned = barcode.trim()
      
      const isGS1 = cleaned.includes('(') || 
                   (cleaned.startsWith('01') && cleaned.length > 16) ||
                   (cleaned.length > 20 && /^\d/.test(cleaned)) ||
                   (cleaned.includes(String.fromCharCode(29)))
      
      if (isGS1) {
        const { gtin, variableData } = this.extractGTINAndVariableData(cleaned)
        const hasVariableData = variableData && Object.keys(variableData).length > 0
        
        return { 
          type: 'GS1', 
          gtin: gtin,
          original: cleaned,
          variableData: variableData,
          hasVariableData: hasVariableData
        }
      }
      
      if (/^\d{13}$/.test(cleaned)) {
        return { type: 'EAN-13', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      if (/^\d{12}$/.test(cleaned)) {
        return { type: 'UPC-A', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      if (/^\d{14}$/.test(cleaned)) {
        return { type: 'ITF-14', gtin: cleaned, original: cleaned, variableData: null }
      }
      
      return { type: 'Other', barcode: cleaned, original: cleaned, variableData: null }
    },

    extractGTINAndVariableData(gs1Code) {
      if (!gs1Code) return { gtin: null, variableData: null }
      
      // Eliminar TODOS los paréntesis (igual que en Egresos.vue)
      // El parser GS1 detectará los AIs sin necesidad de separadores
      let cleaned = gs1Code.replace(/[()]/g, '')
      cleaned = this.normalizeFNC1(cleaned)
      let gtin = null
      let variableData = {}
      
      try {
        console.log('🔧 Código después de limpiar paréntesis y normalizar:', cleaned)
        
        if (cleaned.startsWith('01') && cleaned.length >= 16) {
          gtin = cleaned.substring(2, 16)
          const remainingData = cleaned.substring(16)
          
          console.log('🔍 GTIN extraído:', gtin)
          console.log('🔍 Datos restantes para parsing:', remainingData)
          
          if (remainingData && remainingData.length > 0) {
            variableData = this.parseGS1VariableData(remainingData)
          }
        } else {
          const gtinMatch = cleaned.match(/^01(\d{14})(.*)/)
          if (gtinMatch) {
            gtin = gtinMatch[1]
            const remainingData = gtinMatch[2]
            
            if (remainingData && remainingData.length > 0) {
              variableData = this.parseGS1VariableData(remainingData)
            }
          }
        }
        
        if (!gtin) {
          gtin = cleaned
        }
        
      } catch (error) {
        console.error('❌ Error parsing GS1 code:', error)
        gtin = cleaned
      }
      
      return { gtin, variableData }
    },

    parseGS1Date(dateValue, aiType = 'genérica') {
      if (!dateValue || dateValue.length !== 6) {
        return null
      }
      
      const yy = dateValue.substring(0, 2)
      const mm = dateValue.substring(2, 4)
      let dd = dateValue.substring(4, 6)
      
      if (dd === '00') {
        dd = '01'
      }
      
      const parsedDate = `20${yy}-${mm}-${dd}T00:00:00`
      return parsedDate
    },

    buildAIIndex() {
      const exact = new Map()
      const wild = []
      
      for (const d of this.AI_DEFS) {
        if (d.wildcard) {
          const re = new RegExp("^" + d.key + "\\d")
          wild.push({ re, def: d })
        } else {
          exact.set(d.key, d)
        }
      }
      
      return { exact, wild }
    },

    findAI(s, pos, aiIndex) {
      const chunk4 = s.slice(pos, pos + 4)
      const chunk3 = s.slice(pos, pos + 3)
      const chunk2 = s.slice(pos, pos + 2)
      
      for (const { re, def } of aiIndex.wild) {
        if (re.test(s.slice(pos, pos + 4))) {
          return { key: s.slice(pos, pos + 4), base: def.key, def }
        }
      }
      
      if (aiIndex.exact.has(chunk4)) {
        return { key: chunk4, base: chunk4, def: aiIndex.exact.get(chunk4) }
      }
      if (aiIndex.exact.has(chunk3)) {
        return { key: chunk3, base: chunk3, def: aiIndex.exact.get(chunk3) }
      }
      if (aiIndex.exact.has(chunk2)) {
        return { key: chunk2, base: chunk2, def: aiIndex.exact.get(chunk2) }
      }
      
      return null
    },

    normalizeFNC1(s) {
      if (!s) return s
      
      let result = s
        .replace(/\u001D/g, this.GS)
        .replace(/<FNC1>|<GS>|\|/gi, this.GS)
      
      result = result.replace(/(\d{3,30})0029(?=(11|13|15|17|10|21|22|240|241|242|243|30|37))/g, `$1${this.GS}`)
      
      return result
    },

    // Validar si una secuencia AI es realmente válida
    isValidAISequence(aiMatch, remainingData) {
      if (!aiMatch || !remainingData) return false
      
      const { def } = aiMatch
      
      // Para AIs de longitud fija, verificar que hay suficiente contenido
      if (def.fixed) {
        const requiredLength = def.len
        if (remainingData.length < requiredLength) {
          return false
        }
        
        // Para fechas, verificar formato básico (6 dígitos)
        if (['11', '13', '15', '17'].includes(def.key) && !/^\d{6}/.test(remainingData)) {
          return false
        }
        
        return true
      }
      
      // Para AIs de longitud variable
      if (remainingData.length === 0) {
        return false
      }
      
      // Para AI (10) lote, verificar contenido mínimo razonable
      if (aiMatch.base === '10') {
        if (remainingData.length <= 2) {
          return false
        }
      }
      
      return true
    },

    parseGS1(raw) {
      if (!raw) return { items: [], warnings: [] }
      
      let s = String(raw).trim()
      s = s.replace(/^\](C1|e0|E0|d2|Q3)/i, "")
      s = this.normalizeFNC1(s)
      
      const aiIndex = this.buildAIIndex()
      const items = []
      const warnings = []
      const processedAIs = new Set()
      let i = 0
      
      while (i < s.length) {
        if (s[i] === this.GS) { 
          i++
          continue 
        }
        
        const m = this.findAI(s, i, aiIndex)
        if (!m) {
          warnings.push(`AI desconocido en posición ${i}`)
          break
        }
        
        const { key, base, def } = m
        
        // SOLO procesar AI 17 (vencimiento) y AI 10 (lote)
        const criticalAIs = ['10', '17']
        if (!criticalAIs.includes(base)) {
          i += key.length
          if (def.fixed) {
            const need = def.len
            if (i + need <= s.length) {
              i += need
            } else {
              i = s.length
            }
          } else {
            // Para variables, buscar activamente el siguiente AI crítico (10 o 17)
            const max = def.max ?? 30
            let j = i
            let foundCriticalAI = false
            
            while (j < s.length && (j - i) < max && s[j] !== this.GS) {
              // Verificar si en esta posición hay un AI crítico
              const nextAI = this.findAI(s, j, aiIndex)
              if (nextAI && criticalAIs.includes(nextAI.base) && !processedAIs.has(nextAI.base)) {
                const remainingAfterAI = s.slice(j + nextAI.key.length)
                if (this.isValidAISequence(nextAI, remainingAfterAI)) {
                  i = j
                  foundCriticalAI = true
                  break
                }
              }
              j++
            }
            
            if (!foundCriticalAI) {
              i = j
              if (s[i] === this.GS) i++
            }
          }
          continue
        }
        
        i += key.length
        
        if (processedAIs.has(base)) {
          warnings.push(`AI duplicado ${base} ignorado`)
          break
        }
        
        processedAIs.add(base)
        
        let value = ""
        
        if (def.fixed) {
          const need = def.len
          if (i + need > s.length) {
            value = s.slice(i)
            i = s.length
          } else {
            value = s.slice(i, i + need)
            i += need
          }
        } else {
          // Para AI variable (como AI 10): buscar FNC1 o siguiente AI mientras avanzamos
          const max = def.max ?? 20
          let j = i
          let cutByFNC1 = false
          
          console.log(`🔍 Extrayendo valor para AI ${base}, pos: ${i}, datos: "${s.slice(i, i + 30)}"`)
          
          while (j < s.length && (j - i) < max) {
            if (s[j] === this.GS) {
              console.log(`  ✂️ FNC1 en pos ${j}`)
              cutByFNC1 = true
              break
            }
            
            // Buscar AI en cada posición (después de avanzar al menos 3 caracteres para tener contenido mínimo)
            if ((j - i) >= 3) {
              const next = this.findAI(s, j, aiIndex)
              if (next && !processedAIs.has(next.base)) {
                const remainingAfterAI = s.slice(j + next.key.length)
                
                // Validación estricta: para AIs de fecha (11, 13, 15, 17), verificar que los siguientes 6 caracteres sean una fecha válida
                if (next.def.fixed && ['11', '13', '15', '17'].includes(next.base)) {
                  if (remainingAfterAI.length >= 6 && /^\d{6}/.test(remainingAfterAI)) {
                    const dateStr = remainingAfterAI.substring(0, 6)
                    const yy = parseInt(dateStr.substring(0, 2))
                    const mm = parseInt(dateStr.substring(2, 4))
                    const dd = parseInt(dateStr.substring(4, 6))
                    
                    // Validar que sea una fecha razonable (año 00-99, mes 01-12, día 01-31)
                    if (mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
                      console.log(`  ✅ AI "${next.key}" encontrado en pos ${j} con fecha válida ${dateStr}, cortando`)
                      break
                    } else {
                      console.log(`  ❌ AI "${next.key}" en pos ${j} tiene fecha inválida ${dateStr}, continuando`)
                    }
                  } else {
                    console.log(`  ❌ AI "${next.key}" en pos ${j} no tiene formato de fecha válido, continuando`)
                  }
                } else if (this.isValidAISequence(next, remainingAfterAI)) {
                  console.log(`  ✅ AI "${next.key}" encontrado en pos ${j}, cortando`)
                  break
                }
              }
            }
            
            j++
          }
          
          value = s.slice(i, j)
          console.log(`  📦 Valor extraído: "${value}" (${i} a ${j})`)
          i = j
          if (s[i] === this.GS) i++
        }
        
        items.push({ ai: key, aiBase: base, name: def.name, value })
      }
      
      return { items, warnings }
    },

    parseGS1VariableData(remainingData) {
      const variableData = {}
      
      try {
        console.log('🔍 parseGS1VariableData recibe:', remainingData)
        const result = this.parseGS1(remainingData)
        console.log('📊 parseGS1 devuelve items:', result.items)
        console.log('📊 Items detallados:')
        result.items.forEach((item, idx) => {
          console.log(`  [${idx}] AI: ${item.ai}, aiBase: ${item.aiBase}, name: ${item.name}, value: "${item.value}"`)
        })
        
        for (const item of result.items) {
          switch (item.aiBase) {
            case '10':
              variableData.lot_number = item.value
              break
            case '11':
              const productionDate = this.parseGS1Date(item.value, 'producción')
              if (productionDate) {
                variableData.production_date = productionDate
              }
              break
            case '17':
              const expiryDate = this.parseGS1Date(item.value, 'vencimiento')
              if (expiryDate) {
                variableData.expiry_date = expiryDate
              }
              break
            case '21':
              variableData.serial_number = item.value
              break
            case '30':
              variableData.quantity = item.value
              break
          }
        }
        
      } catch (error) {
        console.error('❌ Error parsing GS1 variable data:', error)
      }
      
      return variableData
    },

    findProductByGTIN(gtin) {
      return findProductByAnyBarcode(this.allProducts, gtin)
    },

    findProductByBarcode(barcode) {
      return findProductByAnyBarcode(this.allProducts, barcode)
    },

    async addScannedProduct(product, variableData, originalCode) {
      console.log(`📦 Agregando producto: ${product.name} (ID: ${product.id})`)
      
      // Validar que hay stock en la ubicación origen
      const hasStock = await this.validateSourceInventory(product.id, variableData)
      
      if (!hasStock) {
        this.showError(`No hay stock disponible para este producto en la ubicación de origen`)
        return
      }
      
      // Buscar duplicados (mismo producto + mismo lote)
      const existingIndex = this.movementForm.items.findIndex(item => 
        item.product_id === product.id &&
        item.lot_number === (variableData.lot_number || null) &&
        item.expiry_date === (variableData.expiry_date || null)
      )
      
      if (existingIndex !== -1) {
        // Incrementar cantidad
        this.movementForm.items[existingIndex].quantity += parseInt(variableData.quantity) || 1
        this.showSuccess(`Cantidad actualizada: ${product.name}`)
        console.log(`♻️ Duplicado - Incrementando cantidad`)
      } else {
        // Agregar nuevo item
        const newItem = {
          product_id: product.id,
          product_name: product.name,
          product_sku: product.sku,
          quantity: parseInt(variableData.quantity) || 1,
          lot_number: variableData.lot_number || null,
          expiry_date: variableData.expiry_date || null,
          serial_number: variableData.serial_number || null,
          barcode_scanned: originalCode
        }
        
        this.movementForm.items.push(newItem)
        this.showSuccess(`Producto agregado: ${product.name}`)
        console.log(`✅ Nuevo producto agregado`)
      }
    },

    async validateSourceInventory(productId, variableData) {
      try {
        console.log('🔍 Validando inventario origen:', {
          productId,
          source_zone_id: this.movementForm.source_zone_id,
          variableData: variableData
        })
        
        // Mostrar todos los items del producto en inventario
        const allProductItems = this.inventoryData.filter(item => item.product?.id === productId)
        console.log(`📦 Total items del producto ${productId} en inventario:`, allProductItems.length)
        allProductItems.forEach(item => {
          console.log(`  - Location ID: ${item.location?.id}, Lote: ${item.lot_number}, Expiry: ${item.expiry_date}, Quantity: ${item.quantity}`)
        })
        
        // Buscar en inventario local
        let inventoryItems = this.inventoryData.filter(item => 
          item.product?.id === productId &&
          item.location?.id === this.movementForm.source_zone_id
        )
        
        console.log(`📍 Items en zona seleccionada (${this.movementForm.source_zone_id}):`, inventoryItems.length)
        inventoryItems.forEach(item => {
          console.log(`  - Lote: ${item.lot_number}, Expiry: ${item.expiry_date}, Quantity: ${item.quantity}`)
        })
        
        // Si hay lote/fecha específica, buscar coincidencia exacta
        if (variableData && (variableData.lot_number || variableData.expiry_date)) {
          console.log('🔍 Filtrando por lote/vencimiento específico')
          inventoryItems = inventoryItems.filter(item => {
            const lotMatch = !variableData.lot_number || 
                           (item.lot_number?.toUpperCase() === variableData.lot_number?.toUpperCase())
            const expiryMatch = !variableData.expiry_date || 
                              (item.expiry_date?.substring(0, 10) === variableData.expiry_date?.substring(0, 10))
            
            console.log(`  - Item: lote=${item.lot_number}, expiry=${item.expiry_date}, lotMatch=${lotMatch}, expiryMatch=${expiryMatch}`)
            return lotMatch && expiryMatch
          })
          
          console.log(`✅ Items después de filtrar por lote/vencimiento:`, inventoryItems.length)
        }
        
        // Verificar si hay stock
        const hasStock = inventoryItems.some(item => item.quantity > 0)
        
        console.log(`${hasStock ? '✅' : '❌'} Stock disponible: ${hasStock}`)
        
        return hasStock
      } catch (error) {
        console.error('Error validating inventory:', error)
        return false
      }
    },

    removeProduct(index) {
      this.movementForm.items.splice(index, 1)
      this.showSuccess('Producto eliminado')
    },

    async saveMovement() {
      if (!this.isMovementFormValid) return

      this.submitting = true
      try {
        const movementData = {
          movement_type: this.movementForm.movement_type,
          notes: this.movementForm.notes,
          details: this.movementForm.items.map(item => ({
            product_id: item.product_id,
            source_location_id: this.movementForm.source_zone_id,  // Zone ID
            destination_location_id: this.movementForm.dest_zone_id,  // Zone ID
            quantity: item.quantity,
            lot_number: item.lot_number,
            expiry_date: item.expiry_date ? item.expiry_date.split('T')[0] : null
          }))
        }

        await api.createMovimientoInterno(movementData)
        this.showSuccess('Movimiento creado correctamente')
        
        this.closeMovementDialog()
        await this.loadMovements()
      } catch (error) {
        console.error('Error saving movement:', error)
        this.showError('Error al guardar el movimiento')
      } finally {
        this.submitting = false
      }
    },

    viewMovement(movement) {
      this.selectedMovement = movement
      this.showViewDialog = true
    },

    async executeMovement(movement) {
      if (!confirm('¿Está seguro de que desea ejecutar este movimiento? Esta acción actualizará el inventario.')) {
        return
      }

      try {
        await api.executeMovimientoInterno(movement.id)
        await this.loadMovements()
        this.showSuccess('Movimiento ejecutado correctamente')
      } catch (error) {
        console.error('Error executing movement:', error)
        this.showError('Error al ejecutar el movimiento')
      }
    },

    async deleteMovement(movement) {
      if (!confirm('¿Está seguro de que desea eliminar este movimiento?')) {
        return
      }

      try {
        await api.deleteMovimientoInterno(movement.id)
        await this.loadMovements()
        this.showSuccess('Movimiento eliminado correctamente')
      } catch (error) {
        console.error('Error deleting movement:', error)
        this.showError('Error al eliminar el movimiento')
      }
    },

    async exportMovements() {
      this.exporting = true
      try {
        const data = this.filteredMovements.map(movement => ({
          Referencia: movement.reference,
          Fecha: this.formatDate(movement.created_at),
          Estado: movement.status,
          Productos: movement.details?.length || 0,
          Notas: movement.notes || ''
        }))

        const worksheet = XLSX.utils.json_to_sheet(data)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Movimientos Internos')
        
        const fileName = `movimientos_internos_${new Date().toISOString().split('T')[0]}.xlsx`
        XLSX.writeFile(workbook, fileName)
        
        this.showSuccess('Datos exportados correctamente')
      } catch (error) {
        console.error('Error exporting data:', error)
        this.showError('Error al exportar los datos')
      } finally {
        this.exporting = false
      }
    },

    // Utility functions
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES')
    },

    getStatusColor(status) {
      const colors = {
        'Pendiente': 'warning',
        'Completado': 'success',
        'Cancelado': 'error'
      }
      return colors[status] || 'grey'
    },

    showSuccess(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'success',
        timeout: 4000
      }
    },

    showError(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'error',
        timeout: 6000
      }
    }
  }
}
</script>

<style scoped>
.movimientos-internos-container {
  padding: 20px;
}

.movement-items-table {
  width: 100%;
}

.movement-items-table th {
  background-color: #f8f9fa !important;
  font-weight: 600 !important;
  color: #495057 !important;
  padding: 12px !important;
}

.movement-items-table td {
  padding: 12px !important;
}

.v-card {
  border-radius: 8px;
}
</style>

